import { AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  BbitCalendarDisplayStyle,
  BbitI18n,
  BbitLanguage,
  BbitTime,
  IBbitCalendar,
  IBbitCalendarDisplayHours,
  IBbitCalendarEvent,
  IBbitCalendarNewEventOptions,
  IBbitCalendarOptions,
} from '@bbitgmbh/bbit.utils';
import { BbitWebsiteService } from '@bbitgmbh/website.sdk';
import { DateTime, Duration } from 'luxon';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { CalendarComponent } from '../booking/calendar/calendar.component';

const newEventOptions: IBbitCalendarNewEventOptions = {
  designation: 'Logout',
  snapGrid: Duration.fromObject({ minutes: 30 }),
  duration: Duration.fromObject({ minutes: 30 }),
  preventOverlapping: false,
  preventInPast: false,
};

@Component({
  selector: 'app-tv',
  imports: [CalendarComponent, FormsModule, AsyncPipe],
  templateUrl: './tv.component.html',
  styleUrl: './tv.component.scss',
})
export class TvComponent {
  public secret = new BehaviorSubject('');
  private _ngUnsubscribe = new Subject<void>();
  private _date = new BehaviorSubject(DateTime.now());
  private _calendars = new BehaviorSubject<IBbitCalendar[]>([]);
  private _events = new BehaviorSubject<IBbitCalendarEvent[]>([]);
  // biome-ignore lint/suspicious/noExplicitAny: required
  private _reloadInterval: any;
  public secretInput = '';
  private _usesSavedToken = false;
  private _website = inject(BbitWebsiteService);
  private _displayHoursSubject = new BehaviorSubject<IBbitCalendarDisplayHours>({ from: 13, to: 22 });

  public options: IBbitCalendarOptions = {
    date: this._date,
    calendars: this._calendars,
    events: this._events,
    displayStyle: BbitCalendarDisplayStyle.BOOKING,
    disableDragDrop: true,
    displayHours: this._displayHoursSubject,
    onDateChanged: async (date: DateTime) => {
      this._date.next(date);
      await this._reload();
    },
    newEventOptions: new BehaviorSubject(newEventOptions),
    onNewEvent: async () => {
      localStorage.removeItem('@bbit/tv/secret');
      location.reload();
    },
  };

  constructor() {
    const saved = localStorage.getItem('@bbit/tv/secret');
    if (saved) {
      this._usesSavedToken = true;
      this.secret.next(saved);
    }
    this.secret.pipe(takeUntil(this._ngUnsubscribe)).subscribe(async (secret) => {
      if (secret?.length > 0) {
        this._reload();
        this._reloadInterval = setInterval(() => this._reload(), 900000);
      } else {
        if (this._reloadInterval) {
          clearInterval(this._reloadInterval);
          this._reloadInterval = null;
        }
      }
    });

    setInterval(
      () => {
        location.reload();
      },
      10 * 60 * 1000,
    );

    this._date.subscribe((date) => {
      const isWeekend = date.weekday === 6 || date.weekday === 7;
      this._displayHoursSubject.next({ from: isWeekend ? 9 : 13, to: 22 });
    });
  }

  private async _reload(): Promise<void> {
    this._date.next(DateTime.now());
    try {
      const url = `${this._website.getBlApiUrl()}/placeBooking/tv/${this.secret.value}`;
      const response = await fetch(url, {
        headers: { authorization: this.secret.value },
      });
      const result = await response.json();

      for (const event of result.events) {
        event.calendar = result.calendars.find((o: IBbitCalendar) => o._id === event.calendar);
        event.from = BbitTime.fromISOTime(event.from).setToDateTime(this._date.value);
        event.until = BbitTime.fromISOTime(event.until).setToDateTime(this._date.value);
      }

      this._calendars.next(
        // biome-ignore lint/suspicious/noExplicitAny: currently required
        result.calendars.map((o: any) => {
          return {
            ...o,
            designation: BbitI18n.t(o.designation, BbitLanguage.DE_CH),
          };
        }),
      );
      this._events.next(result.events);
    } catch {
      if (!this._usesSavedToken) {
        this.secret.next('');
        localStorage.setItem('@bbit/tv/secret', '');
      }
    }
  }

  ngOnDestroy(): void {
    this._ngUnsubscribe.next();
    this._ngUnsubscribe.complete();
  }

  login(): void {
    this.secret.next(this.secretInput);
    localStorage.setItem('@bbit/tv/secret', this.secretInput);
  }
}
